// Story 10.2 — Filtro y orden client-side de bots para el dashboard.
// Pure-logic, sin React, sin TanStack — testeable en isolation.

export interface BotForFilter {
  id: number;
  name: string;
  status: 'inactive' | 'active' | 'error';
  updatedAt: string;
}

export type SortKey = 'name' | 'activity' | 'status';

const STATUS_ORDER: Record<BotForFilter['status'], number> = {
  // Errores arriba, después active, luego inactive — operacionalmente
  // quieres ver problemas primero.
  error: 0,
  active: 1,
  inactive: 2,
};

/**
 * Filtra por substring case-insensitive sobre `name` y ordena.
 * `query` se trim-ea; vacío equivale a "no filtrar".
 */
export function filterAndSortBots<T extends BotForFilter>(
  bots: readonly T[],
  query: string,
  sort: SortKey,
): T[] {
  const trimmed = query.trim().toLocaleLowerCase('es-AR');
  const filtered =
    trimmed.length === 0
      ? [...bots]
      : bots.filter((b) => b.name.toLocaleLowerCase('es-AR').includes(trimmed));

  switch (sort) {
    case 'name':
      filtered.sort((a, b) => a.name.localeCompare(b.name, 'es-AR'));
      break;
    case 'activity':
      filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      break;
    case 'status':
      filtered.sort((a, b) => {
        const oa = STATUS_ORDER[a.status] ?? 99;
        const ob = STATUS_ORDER[b.status] ?? 99;
        if (oa !== ob) return oa - ob;
        return a.name.localeCompare(b.name, 'es-AR');
      });
      break;
  }
  return filtered;
}

/**
 * Story 16.5 — las organizaciones de esos bots, sin repetir y por nombre. El
 * dashboard muestra el selector cuando hay más de una.
 */
export function organizationsOf(
  bots: ReadonlyArray<{ organization: { id: number; name: string } | null }>,
): Array<{ id: number; name: string }> {
  const byId = new Map<number, { id: number; name: string }>();
  for (const b of bots) if (b.organization) byId.set(b.organization.id, b.organization);
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'es-AR'));
}

/** `?org=3` (string o number, según cómo lo parsee el router) → 3. */
export function parseOrganizationFilter(input: unknown): number | undefined {
  const raw = (input as Record<string, unknown> | null)?.org;
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
}

/** Validador de search params del dashboard — strip silently de inválidos. */
export function parseDashboardSearch(input: unknown): { q: string; sort: SortKey } {
  const obj = (input ?? {}) as Record<string, unknown>;
  const q = typeof obj.q === 'string' ? obj.q : '';
  const sortRaw = obj.sort;
  const sort: SortKey =
    sortRaw === 'name' || sortRaw === 'activity' || sortRaw === 'status' ? sortRaw : 'activity';
  return { q, sort };
}
